import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { RefreshCw, Download, Plus, Search, FileText } from 'lucide-react';

import { v3Api } from '../hooks/useApi';
import { formatMinor, formatMoney, getActiveCurrency } from '../Shared/Formatters';
import { StatusPill, PaymentPill, HoldoverPill } from './parts/StatusPill';
import { toCsv } from './invoiceColumns';
import './invoicing.css';

/**
 * Invoice register.
 *
 * Pagination is SERVER-side. The Revenue Share Report slices client-side, which
 * is fine for one month of tenants and hopeless here — 400 tenants x 12 months
 * is ~4,800 invoices, and the totals footer has to cover the whole filtered set
 * rather than the visible page.
 */

const PAGE_SIZE = 25;

export default function InvoiceList() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [totals, setTotals] = useState(null);
    const [skip, setSkip] = useState(0);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);

    const [filters, setFilters] = useState({
        period_key: '',
        status: '',
        q: '',
        holdover: false,
    });

    const canManage = localStorage.getItem('manageInvoicing') === '1'
        || localStorage.getItem('usertype') === 'admin';

    const params = useMemo(() => {
        const p = { limit: PAGE_SIZE, skip };
        if (filters.period_key) p.period_key = filters.period_key;
        if (filters.status) p.status = filters.status;
        if (filters.q) p.q = filters.q;
        if (filters.holdover) p.holdover = 'true';
        return p;
    }, [filters, skip]);

    const load = useCallback(() => {
        setLoading(true);
        v3Api.invoicing.list(params)
            .then((res) => {
                setRows(Array.isArray(res.rows) ? res.rows : []);
                setTotal(res.total || 0);
                setTotals(res.totals || null);
            })
            .catch((e) => toast.error(e.response?.data?.message || t('Failed to load invoices')))
            .finally(() => setLoading(false));
    }, [params, t]);

    useEffect(() => { load(); }, [load]);

    const setFilter = (key, value) => {
        setSkip(0); // a changed filter invalidates the current page offset
        setFilters((f) => ({ ...f, [key]: value }));
    };

    const onExport = () => {
        setExporting(true);
        // Server-side export so the file covers the whole filtered set, not the
        // 25 rows currently in memory.
        v3Api.invoicing.exportCsv(params)
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `invoices_${filters.period_key || 'all'}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(url);
            })
            .catch(() => {
                // Fall back to exporting what is on screen, and say so — a
                // silently partial export of financial data is worse than none.
                try {
                    const csv = toCsv(rows);
                    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `invoices_page_${skip / PAGE_SIZE + 1}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.warn(t('Server export failed — exported only the {{count}} rows on this page.', { count: rows.length }));
                } catch {
                    toast.error(t('Export failed'));
                }
            })
            .finally(() => setExporting(false));
    };

    const currency = rows[0]?.currency || getActiveCurrency();
    const page = Math.floor(skip / PAGE_SIZE) + 1;
    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="v3 v3-inv">
            <div className="v3-inv-header">
                <div>
                    <h1 className="v3-inv-title">{t('Invoices')}</h1>
                    <p className="v3-inv-sub">{t('Tenant invoices generated from lease terms and recorded sales.')}</p>
                </div>
                <div className="v3-inv-actions">
                    <button type="button" className="v3-inv-btn" onClick={load} disabled={loading}>
                        <RefreshCw size={14} /> {t('Refresh')}
                    </button>
                    <button type="button" className="v3-inv-btn" onClick={onExport} disabled={exporting || !total}>
                        <Download size={14} /> {exporting ? t('Exporting…') : t('Export CSV')}
                    </button>
                    {canManage && (
                        <button
                            type="button"
                            className="v3-inv-btn v3-inv-btn-primary"
                            onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/invoicing/generate`)}
                        >
                            <Plus size={14} /> {t('Generate Invoices')}
                        </button>
                    )}
                </div>
            </div>

            {/* Totals cover the WHOLE filtered set, not the visible page. This is
                the one surface where abbreviated money is acceptable. */}
            {totals && (
                <div className="v3-inv-kpis">
                    <div className="v3-inv-kpi">
                        <div className="v3-inv-kpi-label">{t('Invoices')}</div>
                        <div className="v3-inv-kpi-value">{total}</div>
                    </div>
                    <div className="v3-inv-kpi">
                        <div className="v3-inv-kpi-label">{t('Total invoiced')}</div>
                        <div className="v3-inv-kpi-value">{formatMoney(totals.invoiced_minor / 100, currency)}</div>
                    </div>
                    <div className="v3-inv-kpi">
                        <div className="v3-inv-kpi-label">{t('Outstanding')}</div>
                        <div className="v3-inv-kpi-value">{formatMoney(totals.outstanding_minor / 100, currency)}</div>
                    </div>
                    <div className="v3-inv-kpi">
                        <div className="v3-inv-kpi-label">{t('Holdover')}</div>
                        <div className="v3-inv-kpi-value">{totals.holdover_count || 0}</div>
                    </div>
                </div>
            )}

            {totals?.holdover_count > 0 && (
                <div className="v3-inv-banner v3-inv-banner-warn">
                    <span>
                        {t('{{count}} invoice(s) were billed on an expired lease and are withheld from automatic emailing. Confirm the lease renewal before sending each one.', { count: totals.holdover_count })}
                    </span>
                </div>
            )}

            <div className="v3-inv-filters">
                <div className="v3-inv-field">
                    <span className="v3-inv-label">{t('Period')}</span>
                    <input
                        className="v3-inv-input"
                        type="month"
                        value={filters.period_key}
                        onChange={(e) => setFilter('period_key', e.target.value)}
                    />
                </div>
                <div className="v3-inv-field">
                    <span className="v3-inv-label">{t('Status')}</span>
                    <select
                        className="v3-inv-select"
                        value={filters.status}
                        onChange={(e) => setFilter('status', e.target.value)}
                    >
                        <option value="">{t('All')}</option>
                        <option value="GENERATED">{t('Generated')}</option>
                        <option value="SENT">{t('Sent')}</option>
                        <option value="VOID">{t('Void')}</option>
                    </select>
                </div>
                <div className="v3-inv-field" style={{ minWidth: 220 }}>
                    <span className="v3-inv-label">{t('Search')}</span>
                    <input
                        className="v3-inv-input"
                        type="text"
                        placeholder={t('Invoice number, tenant, unit…')}
                        value={filters.q}
                        onChange={(e) => setFilter('q', e.target.value)}
                    />
                </div>
                <div className="v3-inv-field" style={{ minWidth: 0 }}>
                    <span className="v3-inv-label">&nbsp;</span>
                    <label className="v3-inv-btn" style={{ cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={filters.holdover}
                            onChange={(e) => setFilter('holdover', e.target.checked)}
                            style={{ marginInlineEnd: 6 }}
                        />
                        {t('Holdover only')}
                    </label>
                </div>
            </div>

            <div className="v3-inv-card">
                <div className="v3-inv-table-wrap">
                    <table className="v3-inv-table">
                        <thead>
                            <tr>
                                <th>{t('Invoice #')}</th>
                                <th>{t('Tenant')}</th>
                                <th>{t('Unit')}</th>
                                <th>{t('Period')}</th>
                                <th className="v3-inv-num">{t('Days')}</th>
                                <th className="v3-inv-num">{t('Total')}</th>
                                <th>{t('Due Date')}</th>
                                <th>{t('Status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((inv) => (
                                <tr
                                    key={inv._id}
                                    className="v3-inv-row-link"
                                    onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/invoicing/invoice/${inv._id}`)}
                                >
                                    <td>
                                        <FileText size={12} style={{ marginInlineEnd: 5, opacity: 0.6 }} />
                                        {inv.invoice_number || '—'}
                                    </td>
                                    <td>
                                        {inv.bill_to?.store_name || '—'}
                                        <span className="v3-inv-basis">{inv.bill_to?.tenant_code}</span>
                                    </td>
                                    <td>{inv.bill_to?.unit_code || '—'}</td>
                                    <td>{inv.period_key}</td>
                                    <td className="v3-inv-num">{inv.billable_days}</td>
                                    {/* Exact, never abbreviated — this is the payable figure. */}
                                    <td className="v3-inv-num">{formatMinor(inv.total_minor, inv.currency)}</td>
                                    <td>{String(inv.due_date || '').slice(0, 10) || '—'}</td>
                                    <td style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                        <StatusPill status={inv.status} />
                                        <PaymentPill paymentStatus={inv.payment_status} />
                                        <HoldoverPill isHoldover={inv.is_holdover} expiredOn={inv.lease_expired_on} compact />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {!rows.length && !loading && (
                    <div className="v3-inv-empty">
                        <p>{t('No invoices match these filters.')}</p>
                        {canManage && (
                            <button
                                type="button"
                                className="v3-inv-btn"
                                onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/invoicing/generate`)}
                            >
                                {t('Generate Invoices')}
                            </button>
                        )}
                    </div>
                )}
                {loading && <div className="v3-inv-empty">{t('Loading…')}</div>}

                {total > PAGE_SIZE && (
                    <div className="v3-inv-pager">
                        <span>
                            {t('Showing {{from}}–{{to}} of {{total}}', {
                                from: skip + 1,
                                to: Math.min(skip + PAGE_SIZE, total),
                                total,
                            })}
                        </span>
                        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <button
                                type="button"
                                className="v3-inv-btn"
                                disabled={skip === 0 || loading}
                                onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
                            >
                                {t('Previous')}
                            </button>
                            <span>{t('Page {{page}} of {{pages}}', { page, pages })}</span>
                            <button
                                type="button"
                                className="v3-inv-btn"
                                disabled={skip + PAGE_SIZE >= total || loading}
                                onClick={() => setSkip(skip + PAGE_SIZE)}
                            >
                                {t('Next')}
                            </button>
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
